import React, { useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { di } from 'react-magnetic-di';
import { ScreenRedirect } from '@atlassian/jira-screen-redirect/src/ui/ScreenRedirect.tsx';
import { IS_MODERN_SSR } from '@atlassian/jira-conditional-ssr/src/constants.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import type { UserSegmentationRedirect_fragment$key } from './__generated__/UserSegmentationRedirect_fragment.graphql';
import { hasVisitedUserSeg } from './hasVisitedUserSeg.tsx';

const UserSegmentationRedirect = ({
	queryRef,
}: {
	queryRef: UserSegmentationRedirect_fragment$key;
}): React.JSX.Element | null => {
	di(globalThis);

	/**
	 * ⚠️ This is just temporary redirect logic used for testing
	 * while awaiting the implementation of a new query
	 */
	const data = useFragment(
		graphql`
			fragment UserSegmentationRedirect_fragment on Query {
				jira_userSegRedirectAdvice(accountId: $accountId, cloudId: $cloudId) {
					redirect
				}
			}
		`,
		queryRef,
	);

	const currentUrl = globalThis.location.href;
	const userHasVisitedUserSeg = hasVisitedUserSeg(currentUrl);
	const shouldRedirect = !userHasVisitedUserSeg && !!data.jira_userSegRedirectAdvice?.redirect;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (!fg('disable_analytics_error_user_seg')) {
			if (shouldRedirect) {
				fireUIAnalytics(createAnalyticsEvent({}), 'route redirected', {
					destination: 'user-segmentation',
				});
			}
		}
	}, [createAnalyticsEvent, shouldRedirect]);

	if (IS_MODERN_SSR && shouldRedirect) {
		return (
			<ScreenRedirect
				to={`/jira/get-started?continueUrl=${encodeURIComponent(currentUrl)}`}
				persistSearchParams={false}
			/>
		);
	}

	return null;
};

export default UserSegmentationRedirect;
